import React from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import type { PovActivityNode } from '../../../hooks/useSfTeamPovActivitySummary';
import { povCoveragePct, povCoverageRows, povMetricByReportRows } from '../../../support/povActivityCharts';
import CampaignChartCard, { ChartGrid, KpiCards } from '../Campaigns/CampaignChartCard';

interface Props {
  /** `sf/team/pov-activity-summary/` tree rooted at the selected leader; null until loaded or if unavailable. */
  tree: PovActivityNode | null;
  loading: boolean;
  /** The period in words (e.g. "This Fiscal Quarter"). */
  periodLabel: string;
}

/**
 * Manager POV dashboard: the team's totals (their `rollup`: everyone under them), a coverage donut of
 * opportunities with vs without a POV activity, and how each of the three figures splits across their direct reports.
 */
export default function PovMetricsDashboard({ tree, loading, periodLabel }: Props) {
  if (loading) return <HbrSpinner>Loading POV metrics…</HbrSpinner>;
  const total = tree?.rollup;
  if (!tree || !total) return <div className="hbr-css__text-secondary-sm">POV metrics are unavailable right now.</div>;

  const owner = tree.email.toLowerCase();
  const pct = povCoveragePct(total);
  const subtitle = `${periodLabel} — team totals, sub-teams included`;
  const byReport = [
    { metric: 'opportunity_count', title: 'Opportunities by report', empty: 'No opportunities close in this period.', key: 'Opportunities' },
    { metric: 'opps_with_pov_activity_count', title: 'Opps w/ POV Activity by report', empty: 'No opportunities have a POV activity.', key: 'Opps w/ POV Activity' },
    { metric: 'pov_activity_count', title: 'POV SE Activities by report', empty: 'No POV SE activities on these opportunities.', key: 'POV SE Activities' },
  ] as const;

  return (
    <div>
      <KpiCards
        kpis={[
          { label: 'Opportunities', value: total.opportunity_count },
          { label: 'Opps w/ POV Activity', value: total.opps_with_pov_activity_count },
          { label: 'POV SE Activities', value: total.pov_activity_count },
          { label: 'POV Coverage', value: pct == null ? '—' : `${pct}%` },
        ]}
      />
      <ChartGrid>
        <CampaignChartCard
          title="POV coverage"
          subtitle={subtitle}
          chartType="donut_chart"
          data={povCoverageRows(total)}
          tileKey={`pov-coverage-${owner}`}
          tooltipKeys={{ label: 'Opportunities', value: 'Count' }}
          empty="No opportunities close in this period."
        />
        {byReport.map((c) => (
          <CampaignChartCard
            key={c.metric}
            title={c.title}
            subtitle={subtitle}
            chartType="donut_chart"
            data={povMetricByReportRows(tree, c.metric)}
            tileKey={`pov-${c.metric}-${owner}`}
            tooltipKeys={{ label: 'Report', value: c.key }}
            empty={c.empty}
          />
        ))}
      </ChartGrid>
    </div>
  );
}
